import { optimizeExternalImageUrl } from '../../../utils/catalogImageDelivery';

const CDN_BASE = (import.meta.env.VITE_HOME_CDN_BASE as string | undefined)?.replace(
  /\/$/,
  '',
);
const IMAGEKIT_ENDPOINT = (
  import.meta.env.VITE_IMAGEKIT_URL_ENDPOINT as string | undefined
)?.replace(/\/$/, '');

/**
 * Map Pinterest fallback URLs → CDN asset keys under `{VITE_HOME_CDN_BASE}/home/marketing/`.
 * After uploading WebP assets to your bucket, add entries here.
 */
export const HOME_IMAGE_CDN_MAP: Record<string, string> = {
  // Example after CDN migration:
  // 'https://i.pinimg.com/736x/68/fe/f8/68fef885052e09d6a6446269f8b55404.jpg': 'drop-minimalist-mountain',
};

export type HomeImageWidths = 400 | 640 | 800;

function cdnUrl(key: string, width?: HomeImageWidths): string | null {
  if (CDN_BASE) {
    const suffix = width ? `-w${width}` : '';
    return `${CDN_BASE}/home/marketing/${key}${suffix}.webp`;
  }
  if (IMAGEKIT_ENDPOINT) {
    const tr = width ? `tr:w-${width}/` : '';
    return `${IMAGEKIT_ENDPOINT}/${tr}home/${key}.webp`;
  }
  return null;
}

/** Resolve a marketing image URL — CDN when configured, else optimized Pinterest fallback. */
export function resolveHomeImageUrl(fallbackUrl: string): string {
  const key = HOME_IMAGE_CDN_MAP[fallbackUrl];
  if (key) {
    const cdn = cdnUrl(key, 640) ?? cdnUrl(key);
    if (cdn) return cdn;
  }
  return optimizeExternalImageUrl(fallbackUrl);
}

export function buildHomeImageSrcSet(fallbackUrl: string): string | undefined {
  const key = HOME_IMAGE_CDN_MAP[fallbackUrl];
  if (!key || !CDN_BASE) return undefined;
  const parts = ([400, 640, 800] as const)
    .map((w) => {
      const url = cdnUrl(key, w);
      return url ? `${url} ${w}w` : null;
    })
    .filter(Boolean);
  return parts.length ? parts.join(', ') : undefined;
}

export function homeImageSizes(profile: 'hero' | 'card' | 'rail' | 'lookbook'): string {
  switch (profile) {
    case 'hero':
      return '(max-width: 768px) 45vw, 200px';
    case 'rail':
      return '(max-width: 640px) 55vw, 240px';
    case 'lookbook':
      return '(max-width: 768px) 90vw, 420px';
    default:
      return '(max-width: 640px) 90vw, (max-width: 1024px) 45vw, 320px';
  }
}
